import { V3dTools } from '@/v3d/common';
import { backWallOffset, pointLight, room, xShift, yShift, zShift } from '@/Constants/room';
import { Door } from '@/Engine/Door';
import { Window } from '@/Engine/Window';
import { iWalls, sides, xy } from '@/Interfaces/common';
import { IV3D } from '@/Interfaces/v3d';
import { Box3, Group, Object3D, PointLight, Vector3 } from 'three';
import { useStoreData } from '@/Store/storeData';
import { changeTextureCustom } from '@/Engine/changeTexture';
import { OBB } from 'three/examples/jsm/math/OBB';
import { $ } from '@/Engine/state';
import { engineConfig, gridDivisions, gridSize } from '@/Constants/3dConfig';
import { backWallMinDistance } from '@/Constants/common';
import { defaultTextColor, textMaterial } from '@/Engine/Ruler/material';
import { WardrobeObject } from '@/Engine/Wardrobe/WardrobeObject';
import { GridHelper } from '@/Engine/customGridHelper';
interface iDimension {
    width: number;
    height: number;
    depth: number
}

interface iData {
    height: number;
    leftWallWidth: number;
    rightWallWidth: number;
    frontWallWidth: number;
    backWallWidth: number;
    selectedFloorColor:number;
    selectedWallColor:number;
}

interface iPosition {
    x: number;
    y: number;
    z: number;
}

declare let v3d: IV3D;

type LitePos = [number, number];

const wallDepth = 0.1;
const planeGeometry = new v3d.PlaneGeometry(1, 1);
const boxGeometry = new v3d.BoxGeometry(1,1, wallDepth);
const boxSizingGeometry = new v3d.BoxGeometry(1,1, 1);
const boxMaterial = new v3d.MeshBasicMaterial({ color: 0x97B3CC, transparent: false, opacity: 1 });
const boxSizingMaterial = new v3d.MeshBasicMaterial({ color: 0x678f99, transparent: true, opacity: 0.0 });

const planeMaterial = new v3d.MeshBasicMaterial({ color: 0x678f99, transparent: true, opacity: 0.0 });
const planeCeilMaterial = new v3d.MeshBasicMaterial({ color: 0x678f99, transparent: true, opacity: 0.0, side: v3d.BackSide });

const getRaycastPlane = (side: sides | 'ceil' | 'floor') => {

    // const mesh = new v3d.Mesh(boxGeometry, planeMaterial);
    let mesh;
    if (side === 'ceil') {
        mesh =  new v3d.Mesh(planeGeometry, planeCeilMaterial);
    } else {
        mesh = new v3d.Mesh(planeGeometry, planeMaterial);
    }

    mesh.name = side;
    mesh.userData.side = side;
    return mesh;
};

const getDecorWall = (side: sides) => {
    const mesh = new v3d.Mesh(boxGeometry, boxMaterial);
    // mesh.position.z = -wallDepth/2 - 0.01;
    // mesh.name = side;
    return mesh;
};

const getGridWall = (side: sides, sizeX: number, sizeZ: number) => {
    const grid = new GridHelper(sizeX, sizeZ);
    grid.rotation.set(-Math.PI/2, 0, 0);
    // const mesh = new v3d.Mesh(boxGeometry, boxMaterial);
    // mesh.position.z = -wallDepth/2 - 0.01;
    grid.name = side;
    return grid;
};

const getSizingBox = () => {
    const mesh = new v3d.Mesh(boxSizingGeometry, boxSizingMaterial);
    mesh.scale.set(0.25,0.25,0.25);
    return mesh;
};

export class Room {
    private v3dtools;
    public width = 3;
    public height = 2.4;
    public depth = 3;
    public leftWallWidth = 3;
    public rightWallWidth = 3;
    public frontWallWidth = 3;
    public backWallWidth = 3;
    public doors : Array<Door> = [];
    public windows: Array<Window> = [];
    public walls: iWalls;
    public model: Group;
    public floor: Object3D | null;
    public sizingBoxes: Array<Object3D> = [];
    public ceilRaycastPlane: Object3D;
    public floorRaycastPlane: Object3D;
    xShift = xShift;
    yShift = yShift;
    zShift = zShift;
    pointLight: PointLight | null = null;
    raycastPlanes: Array<Object3D>;
    decorWalls: Array<Object3D>;
    gridWalls!: Array<GridHelper> = [];
    obbs: Array<{obb: OBB; uuid: string, side: string}> = [];
    wallTextureName = 'wallpaper_1.jpg';
    floorTextureName = 'floor_yellow.png';
    oneWall = false;
    invisibleWalls: string[] = [];

    constructor(v3dTools: V3dTools) {
        this.v3dtools = v3dTools;
        this.show('wallTransparence', false);
        this.walls = {
            front: {
                model: v3dTools.getObjectByName(room['wallFront']),
                plane: new v3d.Group(),
            },
            back: {
                model: v3dTools.getObjectByName(room['wallBack']),
                plane: new v3d.Group(),
            },
            left: {
                model: v3dTools.getObjectByName(room['wallLeft']),
                plane: new v3d.Group(),
            },
            right: {
                model: v3dTools.getObjectByName(room['wallRights']),
                plane: new v3d.Group(),
            },
        };
        this.floor = v3dTools.getObjectByName(room['floor']);
        if (this.floor) {
            this.floor.material.envMapIntensity = 0.3;
            this.floor.material.reflectivity = 0;
        }

        this.walls.left.plane.rotation.y = Math.PI/2;
        this.walls.right.plane.rotation.y = -Math.PI/2;
        this.walls.back.plane.rotation.y = Math.PI;
        this.walls.left.plane.userData.side = 'left';
        this.walls.right.plane.userData.side = 'right';
        this.walls.back.plane.userData.side = 'back';
        this.walls.front.plane.userData.side = 'front';

        this.model = new Group();
        this.model.add(this.walls.front.plane);
        this.model.add(this.walls.back.plane);
        this.model.add(this.walls.left.plane);
        this.model.add(this.walls.right.plane);

        this.raycastPlanes = [
            getRaycastPlane('front'),
            getRaycastPlane('back'),
            getRaycastPlane('left'),
            getRaycastPlane('right'),
        ];

        this.decorWalls = [
            getDecorWall('front'),
            getDecorWall('back'),
            getDecorWall('left'),
            getDecorWall('right'),
        ];

        // this.gridWalls = [
        //     getGridWall('front', this.frontWallWidth, this.height),
        //     getGridWall('back', this.backWallWidth, this.height),
        //     getGridWall('left', this.leftWallWidth, this.height),
        //     getGridWall('right', this.rightWallWidth, this.height),
        // ];

        this.ceilRaycastPlane = getRaycastPlane('ceil');
        this.model.add(this.ceilRaycastPlane);

        this.floorRaycastPlane = getRaycastPlane('floor');
        this.model.add(this.floorRaycastPlane);

        this.walls.front.plane.add(this.raycastPlanes[0]);
        this.walls.back.plane.add(this.raycastPlanes[1]);
        this.walls.left.plane.add(this.raycastPlanes[2]);
        this.walls.right.plane.add(this.raycastPlanes[3]);

        this.walls.front.plane.add(this.decorWalls[0]);
        this.walls.back.plane.add(this.decorWalls[1]);
        this.walls.left.plane.add(this.decorWalls[2]);
        this.walls.right.plane.add(this.decorWalls[3]);
        //
        // this.walls.front.plane.add(this.gridWalls[0]);
        // this.walls.back.plane.add(this.gridWalls[1]);
        // this.walls.left.plane.add(this.gridWalls[2]);
        // this.walls.right.plane.add(this.gridWalls[3]);
        // this._updateGrid();

        const getIntersectObject = (side:string) => {
            return {
                obb: new OBB(),
                uuid: crypto.randomUUID(),
                side,
            };
        };
        this.obbs.length = 6;
        for (const [ i, side ] of [ 'front', 'back', 'left', 'right', 'ceil', 'floor' ].entries()) {
            this.obbs[i] = getIntersectObject(side);
        }

        this._updateOBB();

        for (let i = 0; i < 4; i++) {
            const box = getSizingBox();
            this.model.add(box);
            this.sizingBoxes.push(box);
        }


        if (pointLight.enabled) {
            this.pointLight = new v3d.PointLight(pointLight.color, pointLight.intensity);
            this.pointLight.castShadow = pointLight.castShadow;
            this.model.add(this.pointLight);
        }
            // this.v3dtools.getObjectByName('wall_transparence').visible = false;
    }

    // Скрывает прозрачный потолок, используемый для raycaster-а
    switchCeil(state: 'enable' | 'disable') {
        if (state === 'disable') {
            this.ceilRaycastPlane.material = planeCeilMaterial;
        } else {
            this.ceilRaycastPlane.material = planeMaterial;
        }
    }

    getObb(excludedSide = '') {
        return this.obbs.filter((obbObj)=>obbObj.side !== excludedSide);
    }

    getWallModel(side: sides) {
        return this.walls[side].model;
    }

    hideWall(side: sides) {
        this.invisibleWalls.push(side);
        this.walls[side].model?.removeFromParent();
        this.walls[side].plane.removeFromParent();
    }

    hideWallColor(side: sides) {
        this.invisibleWalls.push(side);
        this.walls[side].model?.removeFromParent();
        // console.log(this.walls[side].model);
    }

    hideDecorWalls() {
        this.decorWalls.forEach((wall, index)=>{
            wall.removeFromParent();
        });
    }

    showDecorWalls() {
        this.walls.front.plane.add(this.decorWalls[0]);
        this.walls.back.plane.add(this.decorWalls[1]);
        this.walls.left.plane.add(this.decorWalls[2]);
        this.walls.right.plane.add(this.decorWalls[3]);
    }

    oneWallMode(side: sides | 'standard') {
        this.enableAllWalls();
        this.hideDecorWalls();
        // this.showGrid();
        if (side !== 'standard')
            this.hideWallColor(side);
        switch (side) {
            case 'front':
                this.oneWall = true;
                this.hideWall('left');
                this.hideWall('right');
                this.hideWall('back');
                this.floor?.removeFromParent();
                break;
            case 'left':
                this.oneWall = true;
                this.hideWall('front');
                this.hideWall('right');
                this.hideWall('back');
                this.floor?.removeFromParent();
                break;
            case 'right':
                this.oneWall = true;
                this.hideWall('left');
                this.hideWall('front');
                this.hideWall('back');
                this.floor?.removeFromParent();
                break;
            case 'back':
                this.oneWall = true;
                this.hideWall('left');
                this.hideWall('right');
                this.hideWall('front');
                this.floor?.removeFromParent();
                break;
            case 'standard':
                this.hideGrid();
                this.showDecorWalls();
                this.oneWall = false;
        }

    }

    enableAllWalls() {
        this.invisibleWalls = [];
        // this.invisibleWalls.front = true;
        // this.invisibleWalls.left = true;
        // this.invisibleWalls.right = true;
        // this.invisibleWalls.back = true;
        this.model.add(this.walls.right.model);
        this.model.add(this.walls.left.plane);
        this.model.add(this.walls.right.plane);
        this.model.add(this.walls.left.model);
        this.model.add(this.walls.front.plane);
        this.model.add(this.walls.front.model);
        this.model.add(this.walls.back.plane);
        this.model.add(this.walls.back.model);
        this.model.add(this.floor);
    }

    checkIfNotOutOfWall(obj: WardrobeObject) {
        const wallWidth = this.getWidth(obj.side);
        const leftCheck = obj.position.x - obj.width/2 < 0;
        const rightCheck = obj.position.x + obj.width/2 > wallWidth;
        const ceilCheck = obj.position.y > this.height;
        return leftCheck || rightCheck || ceilCheck;
    }

    _updateOBB() {

        // this.decorWalls.forEach((wall, index)=>{
        //     const box = new Box3;
        //     box.expandByObject(wall);
        //     this.obbs[index].obb.fromBox3(box);
        // });

        this.raycastPlanes.forEach((wall, index)=>{
            const box = new Box3;
            box.expandByObject(wall);
            this.obbs[index].obb.fromBox3(box);
        });
        let box = new Box3;
        box.expandByObject(this.ceilRaycastPlane);
        this.obbs[4].obb.fromBox3(box);

        box = new Box3;
        box.expandByObject(this.floorRaycastPlane);
        box.expandByPoint(new Vector3(0, -5,0));
        this.obbs[5].obb.fromBox3(box);

        this.obbs.forEach((obbObj)=>{
            if (!engineConfig.boxHelper) return;
            const helper = new v3d.Mesh(new v3d.BoxGeometry(1, 1, 1, 1, 1, 1), new v3d.MeshBasicMaterial({ wireframe: true }));
            const size = new Vector3();
            obbObj.obb.getSize(size);
            helper.scale.copy(size);
            helper.position.copy(obbObj.obb.center);
            helper.name = 'temp';
            // helper.position.copy(worlPos);
            $.app.scene.add(helper);
        });
    }

    _updateSizingBoxes() {
        //frontRight
        this.sizingBoxes[0].position.set(
            this.width,
            this.height,
            0);
        this.sizingBoxes[0].userData.side = 'frontRight';
        //backRight
        this.sizingBoxes[1].position.set(
            this.width,
            this.height,
            this.rightWallWidth);
        this.sizingBoxes[1].userData.side = 'backRight';
        //backLeft
        this.sizingBoxes[2].position.set(
            0,
            this.height,
            this.leftWallWidth);
        this.sizingBoxes[2].userData.side = 'backLeft';

        //backRightWall
        const right = this.rightWallWidth>this.leftWallWidth;
        const back = this.backWallWidth<this.frontWallWidth;
        if (!this.backWallWidth ||
            Math.abs(this.backWallWidth - this.width) < backWallMinDistance) {
            this.sizingBoxes[3].visible = false;
        } else {
            this.sizingBoxes[3].visible = true;
        }
        if (right && back) {
            this.sizingBoxes[3].position.set(
                this.width-this.backWallWidth,
                this.height,
                this.depth);
            this.sizingBoxes[3].userData.side = 'backRightWall';
        } else {
            this.sizingBoxes[3].position.set(
                this.backWallWidth,
                this.height,
                this.depth);
            this.sizingBoxes[3].userData.side = 'backLeftWall';
        }


    }

    _updateRaycastPlanes() {
        this.raycastPlanes.forEach((plane)=>{
            plane.scale.y = this.height;
            plane.position.y = this.height/2;
        });
        this.raycastPlanes[0].scale.x = this.frontWallWidth;
        this.raycastPlanes[0].position.z = 0.015;
        this.raycastPlanes[0].position.x = this.frontWallWidth/2;
        this.raycastPlanes[1].scale.x = this.backWallWidth;
        this.raycastPlanes[1].position.x = this.backWallWidth/2;
        this.raycastPlanes[2].scale.x = this.leftWallWidth;
        this.raycastPlanes[2].position.x = this.leftWallWidth/2;
        this.raycastPlanes[3].scale.x = this.rightWallWidth;
        this.raycastPlanes[3].position.x = this.rightWallWidth/2;
    }

    _updateDecorWall() {
        this.decorWalls.forEach((box)=>{
            box.scale.y = this.height;
            box.position.y = this.height/2;
            box.position.z = -wallDepth/2;
        });
        this.decorWalls[0].scale.x = this.frontWallWidth;
        this.decorWalls[0].position.x = this.frontWallWidth/2;

        this.decorWalls[1].visible = !!this.backWallWidth;
        this.decorWalls[2].visible = !!this.leftWallWidth;
        this.decorWalls[3].visible = !!this.rightWallWidth;

        this.decorWalls[1].scale.x = this.backWallWidth;
        this.decorWalls[1].position.x = this.backWallWidth/2;
        this.decorWalls[2].scale.x = this.leftWallWidth;
        this.decorWalls[2].position.x = this.leftWallWidth/2;
        this.decorWalls[2].position.z = -wallDepth/2 - 0.01;
        this.decorWalls[3].scale.x = this.rightWallWidth;
        this.decorWalls[3].position.x = this.rightWallWidth/2;
        this.decorWalls[3].position.z = -wallDepth/2 - 0.01;
    }

    showGrid() {
        this._updateGrid();
    }

    hideGrid() {
        this.gridWalls.forEach((grid)=>{
            grid.removeFromParent();
            grid.dispose();
        });
        this.gridWalls = [];
    }

    _updateGrid() {
        this.gridWalls.forEach((grid)=>{
            grid.removeFromParent();
            grid.dispose();
        });
        this.gridWalls = [];
        this.gridWalls = [
            getGridWall('front', this.frontWallWidth, this.height),
            getGridWall('back', this.backWallWidth, this.height),
            getGridWall('left', this.leftWallWidth, this.height),
            getGridWall('right', this.rightWallWidth, this.height),
        ];
        this.gridWalls.forEach((box)=>{
            box.position.y = this.height/2;
            box.position.z = -wallDepth/2;
        });

        this.walls.front.plane.add(this.gridWalls[0]);
        this.walls.back.plane.add(this.gridWalls[1]);
        this.walls.left.plane.add(this.gridWalls[2]);
        this.walls.right.plane.add(this.gridWalls[3]);

        this.gridWalls[0].position.x = this.frontWallWidth/2;

        this.gridWalls[1].visible = !!this.backWallWidth;
        this.gridWalls[2].visible = !!this.leftWallWidth;
        this.gridWalls[3].visible = !!this.rightWallWidth;

        this.gridWalls[1].position.x = this.backWallWidth/2;
        this.gridWalls[2].position.x = this.leftWallWidth/2;
        this.gridWalls[2].position.z = -wallDepth/2 - 0.01;
        this.gridWalls[3].position.x = this.rightWallWidth/2;
        this.gridWalls[3].position.z = -wallDepth/2 - 0.01;
    }

    getTexture = (path:string) => {
        const texture = new v3d.TextureLoader().loadAsync(path);

        return texture;
    };

    async setFloorColor(color: { image:'' }) {
        if (color.image) {
            const texture = await this.getTexture(color.image);
            const floor = this.v3dtools.getObjectByName(room['floor']);
            texture.name = this.floorTextureName;
            changeTextureCustom(floor, this.floorTextureName, texture);
            // this.floorTextureName = texture.name;
        }
    }

    async setWallColor(color:{ image: string, color: string }) {
        if (color.color && color.color.hex) {
            const hex = '#' + color.color.hex.replace('#','');
            textMaterial.color = new v3d.Color(hex);
        } else {
            textMaterial.color = defaultTextColor;
        }
        if (color.image) {
            const texture = await this.getTexture(color.image);
            const wall = this.v3dtools.getObjectByName(room['wallFront']);
            texture.name = this.wallTextureName;
            changeTextureCustom(wall, this.wallTextureName, texture);
            changeTextureCustom(wall, this.wallTextureName, texture);
            // this.wallTextureName = texture.name;
        }
    }

    getWidth(side: sides) {
        switch (side) {
            case 'front':
                return this.frontWallWidth;
            case 'left':
                return this.leftWallWidth;
            case 'right':
                return this.rightWallWidth;
            case 'back':
                return this.backWallWidth;

        }
    }
    getHeight(){
        return this.height;
    }
    async update(data: iData) {
        const floorColor = useStoreData().getDecorById(data.selectedFloorColor);
        const wallColor = useStoreData().getDecorById(data.selectedWallColor);

        if (floorColor) await this.setFloorColor(floorColor);
        if (wallColor) await this.setWallColor(wallColor);
        this.height = data.height;
        this.leftWallWidth = data.leftWallWidth;
        this.rightWallWidth = data.rightWallWidth;
        this.backWallWidth = Math.min(data.backWallWidth, data.frontWallWidth);
        this.frontWallWidth = data.frontWallWidth;
        this.width = this.frontWallWidth;
        this.depth = Math.max(this.leftWallWidth, this.rightWallWidth, 1);
        this.walls.left.plane.position.z = this.leftWallWidth;
        this.walls.right.plane.position.x = this.width;
        this.walls.back.plane.position.z = this.depth;

        if (this.leftWallWidth)
            this.model.add(this.walls.left.model);
        else
            this.walls.left.model.removeFromParent();

        if (this.rightWallWidth)
            this.model.add(this.walls.right.model);
        else
            this.walls.right.model.removeFromParent();

        this.walls.back.model.visible = !!this.backWallWidth;

        this.setDimension({
            height: this.height,
            width: 1,
            depth: this.leftWallWidth + 0.04,
        }, 'wallLeft');
        this.setPosition([ 0, this.leftWallWidth/2 - 0.5 ], 'wallLeft');

        this.setDimension({
            height: this.height,
            width: 1,
            depth: this.rightWallWidth + 0.04,
        }, 'wallRights');
        this.setPosition([ Math.max(this.backWallWidth, this.frontWallWidth) - 1, this.rightWallWidth/2 - 0.5 ], 'wallRights');


        this.setDimension({
            height: this.height,
            width: this.frontWallWidth,
            depth: 1,
        }, 'wallFront');
        this.setPosition([ this.frontWallWidth/2 - 0.5, 0 ], 'wallFront');


        this.setDimension({
            height: this.height,
            width: this.backWallWidth,
            depth: 1,
        }, 'wallBack');

        const right = this.rightWallWidth>this.leftWallWidth;
        const back = this.backWallWidth<this.frontWallWidth;
        if (back && right) {
            this.setPosition([
                this.width - this.backWallWidth/2 - this.xShift,
                Math.max(this.rightWallWidth, this.leftWallWidth) - backWallOffset ], 'wallBack');
            this.walls.back.plane.position.x = this.frontWallWidth;
        } else {
            this.setPosition([
                this.backWallWidth/2 - this.xShift,
                Math.max(this.rightWallWidth, this.leftWallWidth) - backWallOffset ], 'wallBack');
            this.walls.back.plane.position.x = this.backWallWidth;
        }

        this._updateRaycastPlanes();
        this._updateDecorWall();
        this._updateSizingBoxes();
        // this._updateGrid();

        // this.setDimension({
        //     height: this.height,
        //     width: this.width,
        //     depth: this.depth,
        // }, 'ceiling');
        // this.setPosition([
        //     this.width/2 - 0.5,
        //     this.depth/2 - 0.5 ], 'ceiling');

        this.setDimension({
            height: this.height,
            width: this.width,
            depth: this.depth + 0.02,
        }, 'floor');
        this.setPosition([ this.width/2 - 0.5, this.depth/2 - this.zShift ], 'floor');
        this.setPosition([ this.width/2 - 0.5, this.depth/2 - this.zShift ], 'spot');
        // this.setPosition([ this.width/2 - 0.5, this.depth/2 - this.zShift ], 'spot2');
        this.pointLight?.position.set(this.width / 2, this.height - pointLight.topOffset, this.depth /2);

        this.ceilRaycastPlane.position.y = this.height;
        this.ceilRaycastPlane.scale.set(20,20,20);
        this.ceilRaycastPlane.rotation.x = -Math.PI/2;

        this.floorRaycastPlane.position.y = 0;
        this.floorRaycastPlane.scale.set(20,20,20);
        this.floorRaycastPlane.rotation.x = -Math.PI/2;
        this._updateOBB();
    }

    show(partName: keyof typeof room, state: boolean) {
        const obj = this.v3dtools.getObjectByName(room[partName]);
        if (obj) obj.visible = state;
    }

    setPosition(position: LitePos,partName: keyof typeof room) {
        const obj = this.v3dtools.getObjectByName(room[partName]);
        if (obj)
            obj.position.set(
                position[0]+this.xShift, this.yShift, position[1]+this.zShift,
            );
    }
    setDimension(dimension: iDimension, partName: keyof typeof room) {
        if (dimension.width === 0 || dimension.depth === 0) {
            this.show(partName, false);
            return;
        }
        this.show(partName, true);
        this.v3dtools.setMorphFactorByItem(
            this.v3dtools.getObjectByName(room[partName]),
            'width',
            dimension.width - 1,
        );
        this.v3dtools.setMorphFactorByItem(
            this.v3dtools.getObjectByName(room[partName]),
            'height',
            dimension.height - 2.4,
        );
        this.v3dtools.setMorphFactorByItem(
            this.v3dtools.getObjectByName(room[partName]),
            'depth',
            dimension.depth - 1,
        );
    }

    isEmpty(side: sides) {
        // console.log(this.walls[side].plane.children.length);
        return this.walls[side].plane.children.length <= 3;
    }

    addToWall(item: Object3D, side: sides) {
        this.walls[side].plane.add(item);
        item.userData.side = side;
        if (item.userData.parent)
            item.userData.parent.side = side;
    }

    toXYArray(pos: Vector3): xy {
        return [ pos.x, pos.y ];
    }

    getWallWidth(side: sides) {
        switch (side) {
            case 'right':
                return this.rightWallWidth;
            case 'left':
                return this.leftWallWidth;
            case 'front':
                return this.frontWallWidth;
            case 'back':
                return this.backWallWidth;

        }
    }

    getWallHtmlCoords(side: sides | 'frontRight' | 'backRight' | 'backLeft' | 'backRightWall', shift = 0):xy {
        const right = this.rightWallWidth>this.leftWallWidth;
        const back = this.backWallWidth<this.frontWallWidth;
        switch (side) {
            case 'front':
                return this.toXYArray(this.v3dtools.vergeToHtmlCoords(
                    (this.frontWallWidth / 2),
                    this.height,
                    -shift,
                ));
            case 'back':
                if (back && right) {
                    return this.toXYArray(this.v3dtools.vergeToHtmlCoords(
                        this.backWallWidth?(this.width - this.backWallWidth/2):this.width / 2,
                        this.height,
                        this.depth + shift,
                    ));
                } else
                    return this.toXYArray(this.v3dtools.vergeToHtmlCoords(
                        (this.backWallWidth || this.width) / 2 ,
                        this.height,
                        this.depth + shift,
                    ));
            case 'left':
                return this.toXYArray(this.v3dtools.vergeToHtmlCoords(
                    -shift,
                    this.height,
                    this.depth / 2,
                ));
            case 'right':
                return this.toXYArray(this.v3dtools.vergeToHtmlCoords(
                    this.width + shift,
                    this.height,
                    this.depth / 2,
                ));
            case 'frontRight':
                return this.toXYArray(this.v3dtools.vergeToHtmlCoords(
                    this.width,
                    this.height,
                    0,
                ));
            case 'backRight':
                return this.toXYArray(this.v3dtools.vergeToHtmlCoords(
                    this.width,
                    this.height,
                    this.rightWallWidth,
                ));
            case 'backLeft':
                return this.toXYArray(this.v3dtools.vergeToHtmlCoords(
                    0,
                    this.height,
                    this.leftWallWidth,
                ));
            case 'backRightWall':
                if (back && right) {
                    return this.toXYArray(this.v3dtools.vergeToHtmlCoords(
                        this.width - this.backWallWidth,
                        this.height,
                        this.depth + shift,
                    ));
                } else
                    return this.toXYArray(this.v3dtools.vergeToHtmlCoords(
                        this.backWallWidth,
                        this.height,
                        this.depth + shift,
                    ));
        }
    }
}
